/*!

=========================================================
* Comprehend PT - SMS Verification
=========================================================

* Reached from the "Prefer a text message instead?" link on
* VerifyEmailSent.js. Unlike the emailed link, a code has to be typed back in
* by hand, so this is a form rather than a redeem-on-mount landing page.

* The phone number itself is passed via router `location.state`, not the URL
* - it never appears in the address bar, browser history, or analytics. A
* hard refresh loses that state on purpose; landing here with nothing to
* verify sends the user back to request a fresh code.

=========================================================

*/

// reactstrap components
import { Col } from 'reactstrap';

import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../api/GlobalContext';

const RESEND_COOLDOWN_SECONDS = 60;
const REDIRECT_DELAY_MS = 2500;
const CODE_LENGTH = 6;

const VerifyPhone = () => {
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email;

  const [code, setCode] = useState('');
  const [status, setStatus] = useState('entering'); // entering | verifying | success
  const [errorText, setErrorText] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  // Phone starts from the router state handed over by the check-your-email
  // page, but is editable here: "Wrong number?" lets the user correct a typo
  // and get a fresh code without walking back through the whole flow.
  const [phone, setPhone] = useState(location.state?.phone || '');
  const [maskedPhone, setMaskedPhone] = useState(
    location.state?.maskedPhone || '',
  );
  const [showPhoneChange, setShowPhoneChange] = useState(false);
  const [draftPhone, setDraftPhone] = useState('');
  const [phoneChangeError, setPhoneChangeError] = useState('');
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const interval = setInterval(() => {
      setCooldown((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  // Nothing to verify - most likely a refresh, which drops router state.
  // Send them back to request a fresh code rather than showing a dead form.
  if (!email) {
    return (
      <Col lg="5" md="7">
        <div className="cptx-auth">
          <div className="cptx-card">
            <div className="cptx-card-body" style={{ textAlign: 'center' }}>
              <p className="cptx-subtitle">
                We couldn't find a pending text verification. Request a new
                code from the check-your-email page.
              </p>
              <Link to="/auth/verify/sent">
                <button className="cptx-btn-outline">Back</button>
              </Link>
            </div>
          </div>
        </div>
      </Col>
    );
  }

  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = code.trim();
    if (trimmed.length !== CODE_LENGTH || !/^\d+$/.test(trimmed)) {
      setErrorText(`Enter the ${CODE_LENGTH}-digit code we texted you.`);
      return;
    }

    setStatus('verifying');
    setErrorText('');

    apiClient
      .confirmSmsVerification(email, trimmed)
      .then((data) => {
        if (data && 'error' in data) {
          setStatus('entering');
          setErrorText(data['error']);
          return;
        }
        setStatus('success');
        // Same as the emailed link: verifying doesn't sign in. The phone is
        // whatever the user typed, so it doesn't prove they own the account.
        setTimeout(() => navigate('/auth/login'), REDIRECT_DELAY_MS);
      })
      .catch((e) => {
        setStatus('entering');
        setErrorText('Error: ' + e);
      });
  }

  // Commit an edited number: text a fresh code to it, then adopt it as the
  // number we display and resend to. Clears any code already typed against the
  // old number and restarts the cooldown.
  function handleUpdatePhone(e) {
    e.preventDefault();
    const next = draftPhone.trim();
    if (!next) {
      setPhoneChangeError('Enter a phone number.');
      return;
    }

    setPhoneChangeError('');
    setIsUpdatingPhone(true);

    apiClient
      .sendVerificationSms(email, next)
      .then((data) => {
        setIsUpdatingPhone(false);
        if (data && 'error' in data) {
          setPhoneChangeError(data['error']);
          return;
        }
        setPhone(next);
        setMaskedPhone((data && data['phone']) || next);
        setCooldown(RESEND_COOLDOWN_SECONDS);
        setCode('');
        setErrorText('');
        setShowPhoneChange(false);
      })
      .catch((e) => {
        setIsUpdatingPhone(false);
        setPhoneChangeError('Error: ' + e);
      });
  }

  function handleResend() {
    if (!phone) {
      setErrorText('Go back and re-enter your number to get a new code.');
      return;
    }
    setIsResending(true);
    setErrorText('');

    apiClient
      .sendVerificationSms(email, phone)
      .then((data) => {
        setIsResending(false);
        if (data && 'error' in data) {
          setErrorText(data['error']);
          return;
        }
        setCooldown(RESEND_COOLDOWN_SECONDS);
        setCode('');
      })
      .catch((e) => {
        setIsResending(false);
        setErrorText('Error: ' + e);
      });
  }

  return (
    <Col lg="5" md="7">
      <div className="cptx-auth">
        <div className="cptx-card">
          <div className="cptx-card-body">
            <div className="cptx-icon-tile cptx-icon-tile-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect
                  x="7"
                  y="2"
                  width="10"
                  height="20"
                  rx="2"
                  stroke="#2dce89"
                  strokeWidth="1.8"
                />
                <path
                  d="M11 18h2"
                  stroke="#2dce89"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h2 className="cptx-title" style={{ textAlign: 'center' }}>
              {status === 'success' ? 'Phone verified' : 'Enter your code'}
            </h2>
            <p className="cptx-subtitle" style={{ textAlign: 'center' }}>
              {status === 'success' ? (
                "You're verified. Taking you to the sign in page..."
              ) : (
                <>
                  We texted a {CODE_LENGTH}-digit code
                  {maskedPhone ? ` to ${maskedPhone}` : ''}.
                </>
              )}
            </p>

            {status !== 'success' &&
              (!showPhoneChange ? (
                <div style={{ textAlign: 'center', marginBottom: '18px' }}>
                  <button
                    type="button"
                    className="cptx-change-trigger"
                    onClick={() => {
                      setDraftPhone(phone || '');
                      setPhoneChangeError('');
                      setShowPhoneChange(true);
                    }}
                  >
                    Wrong number?
                  </button>
                </div>
              ) : (
                <div className="cptx-change-panel">
                  <div className="cptx-change-head">
                    <h3>Change your number</h3>
                    <button
                      type="button"
                      className="cptx-change-cancel"
                      onClick={() => setShowPhoneChange(false)}
                    >
                      Cancel
                    </button>
                  </div>
                  <input
                    className="cptx-input"
                    type="tel"
                    autoComplete="tel"
                    placeholder="(555) 123-4567"
                    value={draftPhone}
                    onChange={(e) => setDraftPhone(e.target.value)}
                    autoFocus
                  />
                  {phoneChangeError ? (
                    <div className="cptx-error-text">{phoneChangeError}</div>
                  ) : null}
                  <button
                    type="button"
                    className="cptx-btn-primary"
                    onClick={handleUpdatePhone}
                    disabled={isUpdatingPhone}
                  >
                    {isUpdatingPhone ? 'Sending...' : 'Update and resend'}
                  </button>
                  <p className="cptx-change-note">
                    We'll text a new code to this number.
                  </p>
                </div>
              ))}

            {status !== 'success' && (
              <form onSubmit={handleSubmit}>
                <div className="cptx-field">
                  <input
                    className="cptx-input"
                    style={{
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      letterSpacing: '0.5rem',
                      fontFamily: "'Sora', sans-serif",
                      fontWeight: 700,
                    }}
                    placeholder="000000"
                    inputMode="numeric"
                    maxLength={CODE_LENGTH}
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) =>
                      setCode(
                        e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH),
                      )
                    }
                    autoFocus
                  />
                </div>

                {errorText ? (
                  <div className="cptx-error-text" style={{ textAlign: 'center', marginBottom: '14px' }}>
                    {errorText}
                  </div>
                ) : null}

                <button
                  className="cptx-btn-primary"
                  type="submit"
                  disabled={status === 'verifying'}
                >
                  {status === 'verifying' ? 'Checking...' : 'Verify'}
                </button>
                <div style={{ textAlign: 'center', marginTop: '10px' }}>
                  <button
                    type="button"
                    className="cptx-hint-link"
                    disabled={isResending || cooldown > 0}
                    onClick={handleResend}
                  >
                    {isResending
                      ? 'Sending...'
                      : cooldown > 0
                        ? `Resend code in ${cooldown}s`
                        : "Didn't get it? Send a new code"}
                  </button>
                </div>
              </form>
            )}

            <hr className="cptx-hr" />
            <p className="cptx-footer-note">
              <Link to="/auth/login">Back to sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </Col>
  );
};

export default VerifyPhone;
